import { Fragment } from "react";
import type { PropsWithChildren, RefObject } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { XMarkIcon } from "~/ui";
import type { Styled } from "~/utils";

export const MODAL_SIZES = {
  SMALL: "sm",
  LARGE: "lg",
} as const;

export type ModalSize = (typeof MODAL_SIZES)[keyof typeof MODAL_SIZES];

interface ModalProps extends PropsWithChildren<Styled> {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  initialFocus?: RefObject<HTMLButtonElement>;
  size?: ModalSize;
}

export const Modal = ({
  title,
  isOpen,
  onClose,
  initialFocus,
  className,
  children,
  size = MODAL_SIZES.SMALL,
}: ModalProps) => {
  const { t } = useTranslation();

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={initialFocus}
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-white bg-opacity-40 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <DialogPanel
                className={twMerge(
                  "flex items-center justify-center transition-all sm:my-8 sm:w-full sm:max-w-lg",
                  className,
                )}
              >
                <div
                  className={twMerge(
                    "relative flex flex-col rounded-3xl bg-surface-container-highest p-8 text-primary shadow-sm dark:bg-surface-container-default-dark",
                    size === MODAL_SIZES.SMALL && "w-modal-sm",
                    size === MODAL_SIZES.LARGE && "w-modal-lg",
                  )}
                >
                  <button
                    onClick={onClose}
                    className="absolute right-6 top-8 size-6 text-secondary-20 dark:text-primary-90"
                    aria-label={t("general.close")}
                  >
                    <XMarkIcon aria-hidden="true" />
                  </button>
                  <DialogTitle className="mb-3 text-left text-lg text-secondary-20 dark:text-primary">
                    {title}
                  </DialogTitle>
                  {children}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
